import { BaseBusService } from "./BaseBusService.js";
import { LoginDiagnosticResult } from "../../records/LoginDiagnosticResult.js";
import { AvatarGetResult } from "../../../records/results/AvatarGetResult.js";
import { nonEmptyString } from "../../../records/index.js";
import { SESSION_STATUS } from "../../stores/SessionStore.js";

export class SessionService extends BaseBusService {
  constructor({
    bus,
    authBootstrapService,
    accountAuthService,
    sessionStore,
    logger = console,
  } = {}) {
    super({ bus });
    if (!authBootstrapService || !accountAuthService || !sessionStore) {
      throw new Error("SessionService requires authBootstrapService, accountAuthService, sessionStore");
    }
    this._authBootstrapService = authBootstrapService;
    this._accountAuthService = accountAuthService;
    this._sessionStore = sessionStore;
    this._logger = logger;
    this._runtimeConnectSeq = 0;
    this._register("session", "unlock", (payload) => this.unlock(payload));
    this._register("session", "unlockWithDevice", (payload) => this.unlockWithDevice(payload));
    this._register("session", "disableDeviceUnlock", (payload) => this.disableDeviceUnlock(payload));
    this._register("session", "create", (payload) => this.createAccount(payload));
    this._register("session", "linkDevice", (payload) => this.linkDevice(payload));
    this._register("session", "lock", () => this.lock());
    this._register("session", "selectAccount", (payload) => this.selectAccount(payload));
    this._register("session", "inspectBootstrap", () => this.inspectBootstrap());
    this._register("session", "updateProfile", (payload) => this.updateProfile(payload));
    this._register("session", "getOwnAvatar", (payload) => this.getOwnAvatar(payload));
    this._register("session", "revealMnemonic", (payload) => this.revealMnemonic(payload));
    this._register("session", "changePassword", (payload) => this.changePassword(payload));
    this._register("session", "resetPasswordWithMnemonic", (payload) => this.resetPasswordWithMnemonic(payload));
    this._register("session", "exportBackup", (payload) => this.exportBackup(payload));
    this._register("session", "importBackup", (payload) => this.importBackup(payload));
    this._register("session", "restoreWithMnemonic", (payload) => this.restoreWithMnemonic(payload));
    this._register("session", "purgeAccount", (payload) => this.purgeAccount(payload));
  }

  async exportBackup({ password = "" } = {}) {
    const accountId = this._resolveAccountId();
    if (!accountId) throw new Error("exportBackup: no active account");
    return this._accountAuthService.exportBackup({ accountId, password });
  }

  async importBackup({ encryptedBackup = null, mnemonic = "", newPassword = "" } = {}) {
    // Import creates + unlocks a new active account (fresh-device restore).
    // Tear down any stale runtime first, then complete unlock + connect the
    // same way unlock() does.
    this._runtimeConnectSeq += 1;
    await this._disconnectRuntimeForAuthOperation("importBackup");
    this._sessionStore.setUnlocking();
    try {
      const unlocked = await this._accountAuthService.importBackup({ encryptedBackup, mnemonic, newPassword });
      this._completeUnlock(unlocked);
      return this._sessionStore.snapshot();
    } catch (err) {
      const message = err && err.message ? err.message : "Restore failed.";
      if (this._sessionStore.snapshot().status === SESSION_STATUS.UNLOCKING) {
        this._sessionStore.setLocked({ error: message });
      }
      this.bus.emit("session.restore.failed", { message });
      throw err;
    }
  }

  async restoreWithMnemonic({ mnemonic = "", newPassword = "", profileName = "" } = {}) {
    this._runtimeConnectSeq += 1;
    await this._disconnectRuntimeForAuthOperation("recovery restore");
    this._sessionStore.setUnlocking();
    try {
      const unlocked = await this._accountAuthService.restoreWithMnemonic({ mnemonic, newPassword, profileName });
      this._completeUnlock(unlocked);
      return this._sessionStore.snapshot();
    } catch (err) {
      const message = err && err.message ? err.message : "Recovery failed.";
      if (this._sessionStore.snapshot().status === SESSION_STATUS.UNLOCKING) {
        this._sessionStore.setLocked({ error: message });
      }
      this.bus.emit("session.restore.failed", { message });
      throw err;
    }
  }

  async revealMnemonic({ password = "" } = {}) {
    const accountId = this._resolveAccountId();
    if (!accountId) throw new Error("revealMnemonic: no active account");
    if (typeof this._accountAuthService.revealMnemonic !== "function") return null;
    return this._accountAuthService.revealMnemonic({ accountId, password });
  }

  async changePassword({ oldPassword = "", newPassword = "" } = {}) {
    const accountId = this._resolveAccountId();
    if (!accountId) throw new Error("changePassword: no active account");
    // ChangePassword auto-locks the vault; tear down runtime alongside so the
    // UI is in a clean LOCKED state for the next unlock.
    this._runtimeConnectSeq += 1;
    await this._disconnectRuntimeForAuthOperation("changePassword");
    const result = await this._accountAuthService.changePassword({ accountId, oldPassword, newPassword });
    this.bus.emit("session.passwordChanged", { accountId });
    return result;
  }

  async resetPasswordWithMnemonic({ accountId: explicitId = null, mnemonic = "", newPassword = "" } = {}) {
    const accountId = String(explicitId == null ? "" : explicitId).trim() || this._resolveAccountId();
    if (!accountId) throw new Error("resetPasswordWithMnemonic: accountId is required");
    this._runtimeConnectSeq += 1;
    await this._disconnectRuntimeForAuthOperation("resetPasswordWithMnemonic");
    const result = await this._accountAuthService.resetPasswordWithMnemonic({ accountId, mnemonic, newPassword });
    this.bus.emit("session.passwordReset", { accountId });
    return result;
  }

  async purgeAccount({ accountId = null, password = "" } = {}) {
    // Explicit accountId supports purging a LOCKED account (the Phase 6
    // pre-BIP39 re-create migration, where nothing is unlocked). When omitted,
    // fall back to the active account (the delete-from-settings flow).
    const explicitId = String(accountId == null ? "" : accountId).trim();
    const targetId = explicitId || this._resolveAccountId();
    if (!targetId) throw new Error("purgeAccount: no account specified");
    this._runtimeConnectSeq += 1;
    await this._disconnectRuntimeForAuthOperation("purgeAccount");
    const result = await this._accountAuthService.purgeAccount({ accountId: targetId, password });
    this.bus.emit("session.purged", { accountId: targetId });
    return result;
  }

  async init() {
    await this._authBootstrapService.init();
  }

  async inspectBootstrap() {
    const bootstrap = await this._authBootstrapService.inspectBootstrap();
    const snap = this._sessionStore.snapshot();
    const result = new LoginDiagnosticResult({
      diagnostic: bootstrap.diagnostic,
      session: {
        status: snap && snap.status ? String(snap.status) : "",
        error: snap && snap.error ? String(snap.error) : "",
        selectedAccountId: snap && snap.selectedAccountId ? String(snap.selectedAccountId) : "",
        accountListCount: Array.isArray(snap && snap.accountList) ? snap.accountList.length : 0,
        authScreen: this.bus.stores.uiState && typeof this.bus.stores.uiState.snapshot === "function"
          ? String(this.bus.stores.uiState.snapshot().authScreen || "unlock")
          : "unlock",
        showCreateBranch: this.bus.stores.uiState && typeof this.bus.stores.uiState.snapshot === "function"
          ? String(this.bus.stores.uiState.snapshot().authScreen || "unlock") === "create"
          : String(snap && snap.status || "") === SESSION_STATUS.NO_KEYSTORE,
      },
    });
    this.bus.emit("session.bootstrap.inspected", result);
    return result;
  }

  async unlock({ password = "", accountId = null, enableDeviceUnlock = false } = {}) {
    this._sessionStore.setUnlocking();
    try {
      const unlocked = await this._accountAuthService.unlock({
        accountId,
        password,
        enableDeviceUnlock: enableDeviceUnlock === true,
      });
      this._completeUnlock(unlocked);
      return this._sessionStore.snapshot();
    } catch (err) {
      const message = err && err.message ? err.message : "Unlock failed.";
      if (this._sessionStore.snapshot().status === SESSION_STATUS.UNLOCKING) {
        this._sessionStore.setLocked({ error: message });
      }
      this.bus.emit("session.unlock.failed", { message });
      throw err;
    }
  }

  async unlockWithDevice({ accountId = null } = {}) {
    this._sessionStore.setUnlocking();
    try {
      const unlocked = await this._accountAuthService.unlockWithDevice({ accountId });
      this._completeUnlock(unlocked);
      return this._sessionStore.snapshot();
    } catch (err) {
      const message = err && err.message ? err.message : "Device unlock failed.";
      if (this._sessionStore.snapshot().status === SESSION_STATUS.UNLOCKING) {
        this._sessionStore.setLocked({ error: "" });
      }
      this.bus.emit("session.unlock.failed", { message });
      throw err;
    }
  }

  async disableDeviceUnlock({ accountId = null } = {}) {
    const snap = this._sessionStore.snapshot();
    const resolved = accountId || (snap && snap.selectedAccountId) || (snap && snap.accountId) || null;
    const result = await this._accountAuthService.disableDeviceUnlock({ accountId: resolved });
    this.bus.emit("session.deviceUnlock.disabled", { accountId: resolved });
    return result;
  }

  _completeUnlock(unlocked) {
    this._sessionStore.setUnlocked({
      accountId: unlocked && unlocked.accountId ? unlocked.accountId : null,
      deviceId: unlocked && unlocked.deviceId ? unlocked.deviceId : null,
      localInboxId: unlocked && unlocked.localInboxId ? unlocked.localInboxId : null,
      ownerAccountId: unlocked && unlocked.ownerAccountId ? unlocked.ownerAccountId : null,
    });
    this.bus.emit("session.unlocked", this._sessionStore.snapshot());
    const connectSeq = ++this._runtimeConnectSeq;
    this._connectRuntimeAfterUnlock({ connectSeq }).catch((err) => {
      if (this._logger && typeof this._logger.warn === "function") {
        this._logger.warn("runtime connect failed after unlock", err && err.message ? err.message : err);
      }
    });
  }

  async createAccount({ name = "", profileName = "", password = "", confirmPassword = "" } = {}) {
    const resolvedName = nonEmptyString(name) || nonEmptyString(profileName);
    if (String(password) !== String(confirmPassword)) {
      this._sessionStore.setError("Passwords do not match.");
      return null;
    }
    this._sessionStore.setUnlocking();
    try {
      const unlocked = await this._accountAuthService.createAccount({
        profileName: resolvedName,
        password,
      });
      this._sessionStore.setUnlocked({
        accountId: unlocked && unlocked.accountId ? unlocked.accountId : null,
        deviceId: unlocked && unlocked.deviceId ? unlocked.deviceId : null,
        localInboxId: unlocked && unlocked.localInboxId ? unlocked.localInboxId : null,
        ownerAccountId: unlocked && unlocked.ownerAccountId ? unlocked.ownerAccountId : null,
      });
      this.bus.emit("session.unlocked", this._sessionStore.snapshot());
      this.bus.emit("session.authenticated", this._sessionStore.snapshot());
      const connectSeq = ++this._runtimeConnectSeq;
      this._connectRuntimeAfterUnlock({ connectSeq }).catch((err) => {
        if (this._logger && typeof this._logger.warn === "function") {
          this._logger.warn("runtime connect failed after account creation", err && err.message ? err.message : err);
        }
      });
      return this._sessionStore.snapshot();
    } catch (err) {
      const message = err && err.message ? err.message : "Account creation failed.";
      this._sessionStore.setError(message);
      throw err;
    }
  }

  // S10 — link THIS device to an existing account with a device-link code.
  // Mirrors createAccount's UX contract: confirm-password check, sessionStore
  // error slot, and the same unlock -> runtime-connect chain (which boots the
  // DELEGATED chat server through the shipped S9 path).
  async linkDevice({ linkCode = "", name = "", profileName = "", password = "", confirmPassword = "" } = {}) {
    const resolvedName = nonEmptyString(name) || nonEmptyString(profileName);
    if (String(password) !== String(confirmPassword)) {
      this._sessionStore.setError("Passwords do not match.");
      return null;
    }
    if (typeof this._accountAuthService.linkDevice !== "function") {
      this._sessionStore.setError("Device linking requires the Rez desktop app.");
      return null;
    }
    this._sessionStore.setUnlocking();
    try {
      const unlocked = await this._accountAuthService.linkDevice({
        linkCode,
        profileName: resolvedName,
        password,
      });
      this._sessionStore.setUnlocked({
        accountId: unlocked && unlocked.accountId ? unlocked.accountId : null,
        deviceId: unlocked && unlocked.deviceId ? unlocked.deviceId : null,
        localInboxId: unlocked && unlocked.localInboxId ? unlocked.localInboxId : null,
        ownerAccountId: unlocked && unlocked.ownerAccountId ? unlocked.ownerAccountId : null,
      });
      this.bus.emit("session.unlocked", this._sessionStore.snapshot());
      this.bus.emit("session.authenticated", this._sessionStore.snapshot());
      const connectSeq = ++this._runtimeConnectSeq;
      this._connectRuntimeAfterUnlock({ connectSeq }).catch((err) => {
        if (this._logger && typeof this._logger.warn === "function") {
          this._logger.warn("runtime connect failed after device link", err && err.message ? err.message : err);
        }
      });
      return this._sessionStore.snapshot();
    } catch (err) {
      const message = err && err.message ? err.message : "Device linking failed.";
      this._sessionStore.setError(message);
      throw err;
    }
  }

  async updateProfile({ displayName, avatarDataB64 } = {}) {
    const name = typeof displayName === "string" ? displayName.trim() : "";
    if (!name) {
      throw new Error("updateProfile requires non-empty displayName");
    }

    const accountId = this._resolveAccountId();
    if (!accountId) {
      throw new Error("updateProfile: no active account");
    }

    await this._authBootstrapService.setDisplayName(accountId, name);
    this.bus.emit("session.updated", this._sessionStore.snapshot());

    const client = this.bus.runtime && this.bus.runtime.client ? this.bus.runtime.client : null;
    if (client && typeof client.call === "function") {
      const broadcastPayload = { displayName: name };
      if (typeof avatarDataB64 === "string") {
        broadcastPayload.avatarDataB64 = avatarDataB64;
      }
      const result = await client.call("profile.broadcast", broadcastPayload).catch((err) => {
        if (this._logger && typeof this._logger.warn === "function") {
          this._logger.warn("profile broadcast failed after updateProfile", err && err.message ? err.message : err);
        }
        return null;
      });
      if (result && typeof result.avatarFileHash === "string" && result.avatarFileHash.length > 0) {
        await this._authBootstrapService.setAvatarFileHash(accountId, result.avatarFileHash).catch((err) => {
          if (this._logger && typeof this._logger.warn === "function") {
            this._logger.warn("failed to persist avatar hash", err && err.message ? err.message : err);
          }
        });
      }
      if (typeof avatarDataB64 === "string") {
        await this._authBootstrapService.setAvatarDataB64(accountId, avatarDataB64).catch((err) => {
          if (this._logger && typeof this._logger.warn === "function") {
            this._logger.warn("failed to persist avatar data locally", err && err.message ? err.message : err);
          }
        });
        this.bus.emit("session.avatarChanged", { accountId });
      }
    }

    return this._sessionStore.snapshot();
  }

  async getOwnAvatar({ accountId: explicitId } = {}) {
    const accountId = typeof explicitId === "string" && explicitId.trim()
      ? explicitId.trim()
      : this._resolveAccountId();
    if (!accountId) return new AvatarGetResult({ avatarDataB64: "" });
    const avatarDataB64 = await this._authBootstrapService.getAvatarDataB64(accountId);
    return new AvatarGetResult({ avatarDataB64 });
  }

  async _syncAvatarFromServer() {
    const client = this.bus.runtime && this.bus.runtime.client ? this.bus.runtime.client : null;
    if (!client || typeof client.call !== "function") return;

    const accountId = this._resolveAccountId();
    if (!accountId) return;

    const profile = await client.call("profile.getOwn", {});
    const serverHash = typeof profile.avatarFileHash === "string" ? profile.avatarFileHash : "";
    const localHash = await this._authBootstrapService.getAvatarFileHash(accountId);

    if (serverHash === localHash) return;

    // Server reports no avatar but device has one persisted. Local is the
    // source of truth for this device — explicit removal goes through
    // updateProfile({ avatarDataB64: "" }), which clears local directly.
    // Sync must never wipe local based on the server's in-memory view; the
    // server's #ownerAvatarFileHash is null on any cold start before profile
    // meta finishes loading, and a wipe here destroys the user's avatar.
    if (!serverHash) return;

    const fileResult = await client.call("file.get", { fileHashHex: serverHash }).catch((err) => {
      if (this._logger && typeof this._logger.warn === "function") {
        this._logger.warn("avatar file fetch failed during sync", err && err.message ? err.message : err);
      }
      return null;
    });
    if (!fileResult || typeof fileResult.fileDataB64 !== "string" || fileResult.fileDataB64.length === 0) {
      // Don't update local hash without bytes — that would leave hash and
      // data inconsistent and the next sync would falsely report "match".
      return;
    }
    await this._authBootstrapService.setAvatarFileHash(accountId, serverHash);
    await this._authBootstrapService.setAvatarDataB64(accountId, fileResult.fileDataB64);
    this.bus.emit("session.avatarChanged", { accountId });
  }

  selectAccount({ accountId } = {}) {
    this._authBootstrapService.selectAccount({ accountId });
  }

  async lock() {
    this._runtimeConnectSeq += 1;
    await this._disconnectRuntimeForAuthOperation("lock");
    await this._accountAuthService.logout();
    this.bus.emit("session.locked", this._sessionStore.snapshot());
  }

  async _connectRuntimeAfterUnlock({ connectSeq } = {}) {
    const connected = await this.bus.call("runtime", "connect", {});
    if (connectSeq !== this._runtimeConnectSeq) {
      return null;
    }
    const snap = this._sessionStore.snapshot();
    if (snap.status !== SESSION_STATUS.UNLOCKED) {
      return null;
    }
    this._sessionStore.setUnlocked({
      accountId: connected && connected.accountId ? connected.accountId : snap.accountId,
      deviceId: connected && connected.deviceId ? connected.deviceId : snap.deviceId,
      localInboxId: connected && connected.sessionHandles ? connected.sessionHandles.localInboxId : snap.localInboxId,
      ownerAccountId: connected && connected.sessionHandles ? connected.sessionHandles.ownerAccountId : snap.ownerAccountId,
    });
    this.bus.emit("session.runtime.connected", this._sessionStore.snapshot());
    this._syncAvatarFromServer().catch((err) => {
      if (this._logger && typeof this._logger.warn === "function") {
        this._logger.warn("avatar sync after connect failed", err && err.message ? err.message : err);
      }
    });
    return connected;
  }

  _resolveAccountId() {
    const snap = this._sessionStore.snapshot();
    if (snap && snap.selectedAccountId) return String(snap.selectedAccountId).trim();
    if (snap && snap.accountId) return String(snap.accountId).trim();
    return "";
  }

  async _disconnectRuntimeForAuthOperation(operation) {
    try {
      await this.bus.call("runtime", "disconnect", {});
    } catch (err) {
      if (this._logger && typeof this._logger.warn === "function") {
        this._logger.warn("runtime disconnect failed during " + operation, err && err.message ? err.message : err);
      }
      const required = typeof this._accountAuthService.runtimeDisconnectMustSucceed === "function"
        && this._accountAuthService.runtimeDisconnectMustSucceed();
      if (required) {
        if (err && typeof err === "object") err.code = "RUNTIME_DISCONNECT_FAILED";
        throw err;
      }
    }
  }
}
